import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { revertAll } from '../actions';

export interface BrandDetails {
    Id: string;
    PortalName: string;
    CompanyName?: string;
    PrimaryColour: string;
    Logo: string | null;
    FavLogo?: string | null;
    Font: string;
    Status: number;
    IsPrivateLabel: number;
    PortalUrl: string;
    SupportEmail?: string;
    SupportPhoneNumber?: string;
    IsCreditPaymentEnabled?: number;
    IsChatBotEnabled?: number;
    StripePublishableKey?: string;
    CreatedTime: string;
    UpdatedTime: string;
}

export interface IBrandState {
    brandDetails: BrandDetails | null;
    isBrandLoading: boolean;
}

// Read brand from localStorage synchronously at module-load time (before React renders).
// This is the stale-while-revalidate pattern: serve cached brand immediately, then
// refresh silently from the API in the background (see App.tsx fetchBrandDetails).
// Domain-keyed so multi-tenant portals on different subdomains don't share cache.
const BRAND_CACHE_KEY = `brand_cache_${window.location.hostname}`;
const getCachedBrand = (): BrandDetails | null => {
    try {
        const raw = localStorage.getItem(BRAND_CACHE_KEY);
        return raw ? (JSON.parse(raw) as BrandDetails) : null;
    } catch {
        return null;
    }
};
const cachedBrand = getCachedBrand();

const initialState: IBrandState = {
    // If cache exists → use it immediately so the theme renders with correct colors.
    // If no cache (first ever load) → null and show loading until API responds.
    brandDetails: cachedBrand,
    isBrandLoading: !cachedBrand,
};

const brandSlice = createSlice({
    name: 'brand',
    initialState,
    reducers: {
        setBrandDetails: (state, action: PayloadAction<BrandDetails>) => {
            return {
                ...state,
                brandDetails: action.payload,
                isBrandLoading: false,
            };
        },
        setBrandLoading: (state, action: PayloadAction<boolean>) => {
            return {
                ...state,
                isBrandLoading: action.payload,
            };
        },
        clearBrandDetails: state => {
            return {
                ...state,
                brandDetails: null,
                isBrandLoading: false,
            };
        },
    },
    extraReducers: builder =>
        builder.addCase(revertAll, state => ({
            ...initialState,
            brandDetails: state.brandDetails, // Preserve brandDetails on logout
            isBrandLoading: false,
        })),
});

export const { setBrandDetails, setBrandLoading, clearBrandDetails } = brandSlice.actions;
export const brandReducer = brandSlice.reducer;
